import type { WorkspaceAccess, WorkspaceCapabilities } from '@/types/api'

/** What a person must be able to do in the workspace to open a page (the API's WorkspaceCapability). */
export type WorkspaceCapability = keyof WorkspaceCapabilities

/**
 * Every page of a workspace's Intelligence, by route name, with what it needs
 * and where the shell offers it, in the order a person lands on them. The API
 * decides access (S0 §14.2); this table only lets the shell offer what the API
 * would allow. Each P-spec page joins it when it is built: the analysis pages
 * go in the left menu, headed by the Overview (P1 §4.1, §7.4); Intelligence
 * settings are tabs behind the gear at the foot of it (DECISIONS.md #17, #43). A
 * detail page (one opportunity, one report) is reached from its `parent`, and stands for
 * it when switching workspace: the opportunity or report is its own workspace's.
 */
const PAGES = [
  { name: 'workspace-overview', capability: 'intelligence', area: 'analysis', label: 'nav.overview' },
  { name: 'workspace-opportunities', capability: 'intelligence', area: 'analysis', label: 'nav.opportunities' },
  {
    name: 'workspace-opportunity',
    capability: 'intelligence',
    area: 'detail',
    label: 'nav.opportunities',
    parent: 'workspace-opportunities',
  },
  // The action loop (P5, P6), in P1 §4.1's order.
  { name: 'workspace-objectives', capability: 'intelligence', area: 'analysis', label: 'nav.objectives' },
  { name: 'workspace-reports', capability: 'intelligence', area: 'analysis', label: 'nav.reports' },
  { name: 'workspace-report', capability: 'intelligence', area: 'detail', label: 'nav.reports', parent: 'workspace-reports' },
  { name: 'workspace-attention', capability: 'intelligence', area: 'analysis', label: 'nav.attention' },
  { name: 'workspace-members', capability: 'seats', area: 'settings', label: 'workspace.settings.tabs.members' },
  // P1 §8: the clinic's Owners and Admins declare its profile, in an operational workspace (DECISIONS.md #35).
  { name: 'workspace-clinic-profile', capability: 'context', area: 'settings', label: 'workspace.settings.tabs.clinicProfile' },
  // P1 §6.1: the second tab, and the only one a member with a seat sees.
  { name: 'workspace-my-function', capability: 'intelligence', area: 'settings', label: 'workspace.settings.tabs.myFunction' },
  {
    name: 'workspace-transparency',
    capability: 'transparency',
    area: 'settings',
    label: 'workspace.settings.tabs.transparency',
  },
] as const satisfies readonly {
  name: string
  capability: WorkspaceCapability
  area: 'analysis' | 'settings' | 'detail'
  label: string
  parent?: string
}[]

export type IntelligencePage = (typeof PAGES)[number]['name']

/** A page of the left menu (P1 §4.1). */
export type AnalysisPage = Extract<(typeof PAGES)[number], { area: 'analysis' }>['name']

export const isIntelligencePage = (name: unknown): name is IntelligencePage =>
  PAGES.some((page) => page.name === name)

/** The page's name wherever the shell lists it, as its key in `en.ts`. */
export const pageLabel = (page: IntelligencePage): string =>
  PAGES.find((entry) => entry.name === page)?.label ?? ''

const canOpen = (access: WorkspaceAccess, page: IntelligencePage): boolean =>
  PAGES.some((entry) => entry.name === page && access.can[entry.capability])

/** Where entering this workspace's Intelligence lands: the first page the person may open, if any. */
export const landingPage = (access: WorkspaceAccess): IntelligencePage | null =>
  PAGES.find((page) => page.area !== 'detail' && access.can[page.capability])?.name ?? null

/** A detail page's parent, or the page itself. */
const standingFor = (page: IntelligencePage): IntelligencePage => {
  const entry = PAGES.find((candidate) => candidate.name === page)
  return entry && 'parent' in entry ? entry.parent : page
}

/** The page to show in a workspace for someone coming from `page`: the same one if theirs there, else where it lands. */
export const pageInWorkspace = (access: WorkspaceAccess, page: IntelligencePage): IntelligencePage | null =>
  canOpen(access, page) ? page : landingPage(access)

/**
 * Where switching to `workspace` goes from the route named `from`: the same
 * page when it is theirs there (P1 §7.2 keeps the workspace in the URL), else
 * where entering it lands. Null when there is no page to open there.
 */
export const switchTarget = (workspace: WorkspaceAccess, from: unknown): IntelligencePage | null =>
  isIntelligencePage(from) ? pageInWorkspace(workspace, standingFor(from)) : landingPage(workspace)

/** The left menu's pages this person may open, in order: the Overview first (P1 §4.1, §7.4). */
export const analysisPages = (access: WorkspaceAccess): AnalysisPage[] =>
  PAGES.filter(
    (page): page is Extract<(typeof PAGES)[number], { area: 'analysis' }> =>
      page.area === 'analysis' && access.can[page.capability],
  ).map((page) => page.name)

/** The Intelligence settings tabs this person may see, in order (P1 §4.1, §6). */
export const settingsTabs = (access: WorkspaceAccess): IntelligencePage[] =>
  PAGES.filter((page) => page.area === 'settings' && access.can[page.capability]).map((page) => page.name)

/**
 * Why the API would refuse `capability`, as its error code, so a refused link
 * says the same thing the API does (WorkspaceAccessService's refusal).
 */
export const refusalCode = (
  access: WorkspaceAccess,
  capability: WorkspaceCapability,
): 'INTELLIGENCE_NOT_ENABLED' | 'INTELLIGENCE_ACCESS_REQUIRED' | 'ATTESTATION_IN_DEMONSTRATION' | 'CONTEXT_DECLARER_REQUIRED' => {
  if (capability === 'context' && access.dataProvenanceMode === 'DEMONSTRATION') return 'ATTESTATION_IN_DEMONSTRATION'
  if (capability !== 'transparency' && access.intelligenceStatus !== 'ENABLED') return 'INTELLIGENCE_NOT_ENABLED'
  if (capability === 'context' && access.can.intelligence) return 'CONTEXT_DECLARER_REQUIRED'
  return 'INTELLIGENCE_ACCESS_REQUIRED'
}
